/**
 * Helper utility functions
 */
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

/**
 * Format date to readable string
 */
export const formatDate = (date: string | Date): string => {
  return dayjs(date).format('MMM D, YYYY');
};

/**
 * Format date to relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (date: string | Date): string => {
  return dayjs(date).fromNow();
};

/**
 * Format hours to readable string
 */
export const formatHours = (hours: number): string => {
  if (hours < 1) {
    return `${Math.round(hours * 60)}m`;
  }
  return `${hours}h`;
};

/**
 * Get initials from name
 */
export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Get color class for priority
 */
export const getPriorityColor = (priority: string): string => {
  const colors: Record<string, string> = {
    low: 'text-green-600 bg-green-100',
    medium: 'text-yellow-600 bg-yellow-100',
    high: 'text-orange-600 bg-orange-100',
    critical: 'text-red-600 bg-red-100',
  };
  return colors[priority] || colors.medium;
};

/**
 * Get color class for status
 */
export const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    todo: 'text-gray-600 bg-gray-100',
    in_progress: 'text-blue-600 bg-blue-100',
    in_review: 'text-purple-600 bg-purple-100',
    done: 'text-green-600 bg-green-100',
    backlog: 'text-gray-500 bg-gray-50',
  };
  return colors[status] || colors.todo;
};

/**
 * Get color class for task type
 */
export const getTaskTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    story: 'text-blue-600 bg-blue-100',
    bug: 'text-red-600 bg-red-100',
    task: 'text-gray-600 bg-gray-100',
    epic: 'text-purple-600 bg-purple-100',
  };
  return colors[type] || colors.task;
};

/**
 * Truncate text to specified length
 */
export const truncate = (text: string, length: number): string => {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
};

/**
 * Calculate completion percentage
 */
export const calculateCompletionPercentage = (
  completed: number,
  total: number
): number => {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
};

/**
 * Generate random color for avatar
 */
export const getRandomColor = (seed: string): string => {
  const colors = [
    'bg-red-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
  ];
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Debounce function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};


